/* =====================================================================
 * Plan — 플랜/가격/역할 타입 및 상수 정의
 * ===================================================================== */

/* -- 플랜 타입 -- */
export type PlanType = 'free' | 'pro' | 'premium' | 'max' | 'business' | 'enterprise';

/* -- 사용자 역할 -- */
export type UserRole = 'owner' | 'admin' | 'user';

/* -- 결제 주기 -- */
export type BillingCycle = 'monthly' | 'yearly';

export type PlanFeature =
  | { id: 'tokens'; value: number }
  | { id: 'storage'; value: number }
  | { id: 'basicAgentAccess' }
  | { id: 'communitySupport' }
  | { id: 'includesPlan'; plan: PlanType }
  | { id: 'teamSharedChatAndRules' }
  | { id: 'centralizedTeamBilling' }
  | { id: 'usageAnalyticsAndReporting' }
  | { id: 'organizationPrivacyControls' }
  | { id: 'roleBasedAccessControl' }
  | { id: 'samlOidcSso' }
  | { id: 'integratedUsagePool' }
  | { id: 'invoicePoBilling' }
  | { id: 'scimSeatManagement' }
  | { id: 'fineGrainedAdminModelControls' }
  | { id: 'prioritySupportAndAccountManagement' };

/* -- 플랜 정보 -- */
export interface PlanInfo {
  type: PlanType;
  name: string;
  monthlyPrice: number;       // USD (0 = free, -1 = contact sales)
  tokens: number;             // 총 토큰 한도
  storageGB: number;          // 스토리지 한도 (GB)
  teamStorageGB?: number;     // 팀 스페이스 스토리지 한도 (GB)
  teamSpaceLimit?: number;    // 보유 가능한 팀 스페이스 개수(소유/참여 합산)
  features: PlanFeature[];
  recommended?: boolean;
  category: 'individual' | 'business';
}

/* -- 플랜 상수 맵 -- */
export const PLANS: Record<PlanType, PlanInfo> = {
  free: {
    type: 'free',
    name: 'Free',
    monthlyPrice: 0,
    tokens: 10_000_000,
    storageGB: 1,
    teamStorageGB: 1,
    teamSpaceLimit: 1,
    features: [
      { id: 'tokens', value: 10_000_000 },
      { id: 'storage', value: 1 },
      { id: 'basicAgentAccess' },
      { id: 'communitySupport' },
    ],
    category: 'individual',
  },
  pro: {
    type: 'pro',
    name: 'Pro',
    monthlyPrice: 20,
    tokens: 20_000_000,
    storageGB: 20,
    teamStorageGB: 10,
    teamSpaceLimit: 5,
    features: [
      { id: 'tokens', value: 20_000_000 },
      { id: 'storage', value: 20 },
      { id: 'includesPlan', plan: 'free' },
    ],
    category: 'individual',
  },
  premium: {
    type: 'premium',
    name: 'Premium',
    monthlyPrice: 60,
    tokens: 100_000_000,
    storageGB: 100,
    teamStorageGB: 50,
    teamSpaceLimit: 10,
    features: [
      { id: 'tokens', value: 100_000_000 },
      { id: 'storage', value: 100 },
      { id: 'includesPlan', plan: 'pro' },
    ],
    recommended: true,
    category: 'individual',
  },
  max: {
    type: 'max',
    name: 'Max',
    monthlyPrice: 200,
    tokens: 500_000_000,
    storageGB: 500,
    teamStorageGB: 100,
    teamSpaceLimit: 20,
    features: [
      { id: 'tokens', value: 500_000_000 },
      { id: 'storage', value: 500 },
      { id: 'includesPlan', plan: 'pro' },
    ],
    category: 'individual',
  },
  business: {
    type: 'business',
    name: 'Business',
    monthlyPrice: 40,
    tokens: 100_000_000,
    storageGB: 100,
    teamStorageGB: 100,
    teamSpaceLimit: 20,
    features: [
      { id: 'includesPlan', plan: 'pro' },
      { id: 'teamSharedChatAndRules' },
      { id: 'centralizedTeamBilling' },
      { id: 'usageAnalyticsAndReporting' },
      { id: 'organizationPrivacyControls' },
      { id: 'roleBasedAccessControl' },
      { id: 'samlOidcSso' },
    ],
    category: 'business',
  },
  enterprise: {
    type: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: -1, // contact sales
    tokens: 0,
    storageGB: 0,
    teamStorageGB: 100,
    teamSpaceLimit: 20,
    features: [
      { id: 'includesPlan', plan: 'business' },
      { id: 'integratedUsagePool' },
      { id: 'invoicePoBilling' },
      { id: 'scimSeatManagement' },
      { id: 'fineGrainedAdminModelControls' },
      { id: 'prioritySupportAndAccountManagement' },
    ],
    category: 'business',
  },
};

/* -- 개인 플랜 순서 -- */
export const INDIVIDUAL_PLANS: PlanType[] = ['free', 'pro', 'premium', 'max'];

/* -- 비즈니스 플랜 순서 -- */
export const BUSINESS_PLANS: PlanType[] = ['business', 'enterprise'];

/* -- 포맷 헬퍼 -- */

/** 토큰 수를 읽기 좋은 형태로 (1000000 -> "1M") */
export function formatTokens(n: number): string {
  if (n < 0) return 'Custom';
  if (n === 0) return '0';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(n % 1_000_000_000 === 0 ? 0 : 1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

/** 바이트를 GB/MB로 포맷 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

/** GB 수를 바이트로 변환 */
export function gbToBytes(gb: number): number {
  return gb * 1024 * 1024 * 1024;
}

/** 가격 포맷 ($20 -> "$20") */
export function formatPrice(price: number): string {
  if (price <= 0) return 'Free';
  return `$${price % 1 === 0 ? price : price.toFixed(2)}`;
}

/** 플랜 이름의 대문자 라벨 */
export function planLabel(plan: PlanType): string {
  return PLANS[plan]?.name.toUpperCase() ?? plan.toUpperCase();
}
